import Rollbar from 'rollbar';

let rollbarInstance: Rollbar | null = null;

// getRollbar() is called on every logError/logInfo call site, and when no
// token is configured it returns null without memoising anything - so the
// "missing token" warning fired on every single call and buried the rest of
// the dev console. Warn once per page load instead; the condition it reports
// is static for the lifetime of the process.
let hasWarnedAboutMissingToken = false;

/**
 * Get the singleton Rollbar instance, initialized dynamically on client or server.
 */
export const getRollbar = (): Rollbar | null => {
  if (rollbarInstance) {
    return rollbarInstance;
  }

  const isBrowser = typeof window !== 'undefined';
  const token = isBrowser
    ? process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN
    : process.env.ROLLBAR_SERVER_TOKEN;

  const env = process.env.NEXT_PUBLIC_ROLLBAR_ENV || 'development';

  if (!token) {
    // Gracefully return null if not configured
    if (process.env.NODE_ENV === 'development' && !hasWarnedAboutMissingToken) {
      hasWarnedAboutMissingToken = true;
      console.warn('Rollbar access token is missing in environment variables.');
    }
    return null;
  }

  rollbarInstance = new Rollbar({
    accessToken: token,
    captureUncaught: isBrowser, // Let Rollbar automatically hook uncaught window errors on browser
    captureUnhandledRejections: isBrowser,
    payload: {
      environment: env,
      client: isBrowser ? {
        javascript: {
          source_map_enabled: true,
          code_version: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
        }
      } : undefined
    },
    // Filter sensitive fields and headers
    scrubFields: [
      'password', 'password_confirmation', 'token', 'accessToken',
      'cookie', 'session', 'card', 'cvv', 'cardNumber', 'auth',
      'authorization', 'secret'
    ],
    scrubHeaders: ['Authorization', 'Cookie', 'Set-Cookie'],
  });

  return rollbarInstance;
};

/**
 * Helper function to report manual errors to Rollbar.
 */
export const logErrorToRollbar = (error: any, context?: any) => {
  const rollbar = getRollbar();
  if (rollbar) {
    rollbar.error(error, context);
  } else {
    console.error('Error (Rollbar not initialized):', error, context);
  }
};

/**
 * HTTP status codes that represent expected, normal business/client
 * responses rather than application bugs — expired sessions, permission
 * checks, missing resources, conflicts, and validation failures are all
 * routine outcomes the UI is already built to handle, not defects to page
 * an engineer for on every occurrence.
 *
 * Deliberately NOT included:
 *   - 400 Bad Request — the backend's own convention reserves 422 for
 *     expected business/validation responses (confirmed: the only 400 in
 *     the entire backend is a Stripe webhook signature check, never
 *     reachable from this frontend). A 400 here almost certainly means a
 *     malformed request was constructed client-side, not an expected
 *     business rejection — report it.
 *   - any 5xx (always a real server-side failure)
 *   - any other 4xx not reviewed here (e.g. 429) — those default to being
 *     reported, since an unreviewed status code is more likely a genuine
 *     problem than a known, intentional business response.
 */
const EXPECTED_HTTP_STATUSES = new Set([401, 403, 404, 409, 422]);

type ApiErrorLike = {
  response?: { status?: number };
  code?: string;
  name?: string;
  message?: string;
  config?: { method?: string; url?: string; requestId?: string };
};

/**
 * Cross-browser BACKSTOP for the navigation-abort case. The primary,
 * deterministic signal lives in shouldReportApiError(): Chromium (verified
 * against this repo's axios build) surfaces a request the browser cancels on
 * navigation through XHR's `onabort` handler as `ECONNABORTED` + message
 * "Request aborted", which we match directly. Some engines have historically
 * routed an aborted XHR through `onerror` instead (→ `ERR_NETWORK`, no
 * `response`), which is byte-for-byte identical to a genuine "backend
 * unreachable" error — for that case only, we fall back to observing the
 * teardown itself.
 *
 * Only genuine teardown may arm this latch — see the listener choices below —
 * so a `ERR_NETWORK` while the page is still live (a real outage) is never
 * swallowed.
 */
let pageIsUnloading = false;

if (typeof window !== 'undefined') {
  const markUnloading = () => {
    pageIsUnloading = true;
  };
  // Only genuine teardown flips this. `pagehide` is the reliable signal for
  // every navigation away (reload, tab close, same- and cross-origin nav) and
  // the one mobile browsers still fire; `beforeunload` is an earlier
  // belt-and-suspenders for the same transitions.
  //
  // Deliberately NOT `visibilitychange`/`hidden`: switching tabs or
  // backgrounding the browser hides the page WITHOUT unloading it, and a
  // hidden page keeps its in-flight XHRs running to completion — there is no
  // abort to suppress there. Latching on "hidden" would instead swallow any
  // genuine response-less failure that happened while the tab sat in the
  // background, which is exactly the failure Rollbar must still see.
  window.addEventListener('pagehide', markUnloading);
  window.addEventListener('beforeunload', markUnloading);
  // A page frozen into the bfcache on `pagehide` can be restored by a
  // back/forward navigation, firing `pageshow`. Once it is live again it must
  // resume reporting genuine failures, so clear the flag rather than leaving
  // it latched on for the rest of the restored page's lifetime.
  window.addEventListener('pageshow', () => {
    pageIsUnloading = false;
  });
}

/**
 * Centralized policy for whether an HTTP client error is worth reporting to
 * Rollbar. Shared by every HTTP client's error handling so the
 * expected-vs-unexpected distinction lives in exactly one place.
 *
 * Reported (real bugs / unexpected failures):
 *   - No response at all — network failure, DNS/CORS failure, or timeout
 *   - 5xx server errors
 *   - Any status not in EXPECTED_HTTP_STATUSES
 *
 * Suppressed (expected business/client responses, not defects):
 *   - 400, 401, 403, 404, 409, 422
 *
 * A call site that knows a specific "expected" response actually indicates
 * a defect in that context can still bypass this policy and call
 * logErrorToRollbar() directly.
 */
export function shouldReportApiError(error: ApiErrorLike): boolean {
  const status = error.response?.status;

  // Client-initiated cancellation (AbortController / axios CancelToken) is
  // never a defect — the request was deliberately dropped, so there is
  // nothing to page an engineer for.
  if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
    return false;
  }

  // Browser-cancelled request (the reported /api/languages case): when the
  // browser aborts an in-flight XHR — navigating away, reloading, closing the
  // tab — axios's `onabort` path yields ECONNABORTED + "Request aborted".
  // This is deterministic and distinct from the two other ECONNABORTED-ish
  // outcomes we DO want reported: a genuine timeout carries "timeout of Nms
  // exceeded", and a real connection failure surfaces as ERR_NETWORK /
  // "Network Error". `onabort` never fires for a server or network fault, so
  // this is definitionally a cancellation, not an outage.
  if (error.code === 'ECONNABORTED' && error.message === 'Request aborted') {
    return false;
  }

  // Cross-browser backstop for engines that route a navigation-abort through
  // `onerror` (→ ERR_NETWORK, indistinguishable from a real outage): suppress
  // a response-less error only while a genuine teardown is in progress. A
  // response-less error on a still-live page falls through and is reported.
  if (status === undefined && pageIsUnloading) {
    return false;
  }

  // If the browser/client itself is offline (e.g. device lost internet connection,
  // laptop lid closed, airplane mode, or network dropped), status is undefined and
  // navigator.onLine is false. This is a local client connectivity state, not an
  // application or backend defect.
  if (status === undefined && typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }

  // No response received at all: network failure or request timeout —
  // always a real, actionable problem, never a business response.
  if (status === undefined) {
    return true;
  }

  if (status >= 500) {
    return true;
  }

  return !EXPECTED_HTTP_STATUSES.has(status);
}

/**
 * Reports an HTTP client error to Rollbar according to shouldReportApiError()
 * above — use this from any HTTP client's error interceptor instead of
 * duplicating the expected/unexpected classification at each call site.
 */
export function reportApiErrorToRollbar(error: ApiErrorLike, extraContext?: Record<string, unknown>): void {
  if (!shouldReportApiError(error)) {
    return;
  }

  const status = error.response?.status;
  const method = error.config?.method?.toUpperCase();
  const url = error.config?.url;
  const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';

  const label = status !== undefined
    ? `API Error: ${method} ${url} returned ${status}`
    : isTimeout
      ? `API Timeout: ${method} ${url}`
      : `API Network Error: ${method} ${url}`;

  logErrorToRollbar(new Error(label), {
    status,
    url,
    method,
    ...extraContext,
  });
}

/**
 * Wrapper for Server Actions to automatically report server-side exceptions to Rollbar.
 */
export function withServerAction<T extends (...args: any[]) => Promise<any>>(action: T): T {
  return (async (...args: Parameters<T>): Promise<ReturnType<T>> => {
    try {
      return await action(...args);
    } catch (error) {
      logErrorToRollbar(error, { context: 'server-action' });
      throw error; // Rethrow to let Next.js handle it
    }
  }) as unknown as T;
}

/**
 * Wrapper for Route Handlers (API routes) to automatically report server-side exceptions to Rollbar.
 */
export function withRouteHandler<T extends (req: Request, ...args: any[]) => Promise<Response>>(handler: T): T {
  return (async (req: Request, ...args: any[]): Promise<Response> => {
    try {
      return await handler(req, ...args);
    } catch (error) {
      logErrorToRollbar(error, {
        context: 'route-handler',
        url: req.url,
        method: req.method,
      });
      throw error;
    }
  }) as unknown as T;
}
